import {SingletonService} from '@/common/dependency.configs'
import {isUndefined} from 'lodash'
import NotificationFactory from '@/common/services/notificationFactory'
import {inject, injectable} from 'inversify'
import FACTORY_MSG from '@/common/messages/factory.messages'
import HttpHandler from '@/common/services/connect/HttpHandler'
import ResponseTypes from '@/common/enums/responseTypesEnum'

@SingletonService
@injectable()
class PrintFactory {
  constructor(
    @inject(NotificationFactory)
    private notificationFactory: NotificationFactory,
    @inject(HttpHandler)
    private httpHandler: HttpHandler
  ) {}

  private async openBlobWindow(url: string): Promise<{printWindow: Window; blobUrl: string} | null> {
    const blobConfig = {responseType: 'blob'} as any
    const response = await this.httpHandler.get(url, blobConfig, ResponseTypes.Resource)
    const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data], {type: 'application/pdf'})
    const blobUrl = URL.createObjectURL(blob)
    const printWindow = window.open(blobUrl, '_blank')

    if (printWindow === null || isUndefined(printWindow)) {
      URL.revokeObjectURL(blobUrl)
      this.notificationFactory.error(FACTORY_MSG.PRINT_TOKEN.PRINT_TOKEN_LOG_ERROR)
      return null
    }

    return {printWindow, blobUrl}
  }

  async printAdminPdf(disclosureCode: string) {
    const token = await this.httpHandler.get(
      `{{boltsbranch}}/disclosure/${disclosureCode}/pdf/token`,
      {},
      ResponseTypes.Payload
    )
    const encodedToken = encodeURIComponent(token)
    const windowWithBlob = await this.openBlobWindow(`{{boltsbranch}}/disclosure/pdf?token=${encodedToken}`)
    if (!windowWithBlob) return

    const cleanupInterval = setInterval(() => {
      if (windowWithBlob.printWindow.closed) {
        URL.revokeObjectURL(windowWithBlob.blobUrl)
        clearInterval(cleanupInterval)
      }
    }, 100)
  }

  async printByToken(token: string) {
    const encodedToken = encodeURIComponent(token)
    const windowWithBlob = await this.openBlobWindow(`{{boltsbranch}}/print/pdf?token=${encodedToken}`)
    if (!windowWithBlob) return

    return new Promise<void>((res) => {
      /**
       * There is a race condition where if you print a disclosure, you need to wait for the token to be
       * used before you can run validation rules to check if a user printed the disclosure. Since the
       * print modal might be on a different domain, I can't use addEventListener, so instead, we are
       * waiting for the user to close the print modal OR go back to the original app before we finish
       * this promise.
       */
      const interval = setInterval(() => {
        if (windowWithBlob.printWindow.closed) {
          URL.revokeObjectURL(windowWithBlob.blobUrl)
          res()
          clearInterval(interval)
        }
      }, 100)
    })
  }
}
export default PrintFactory
